import { Link } from 'react-router-dom';

import { LEGAL_CONTACT, LEGAL_DOCUMENTS } from '@/features/legal/documents';
import { paths } from '@/routes/paths';

/**
 * The site footer: the policy documents, and who to contact.
 *
 * It is rendered by AppShell on every customer page rather than only on the home
 * page, for two reasons that pull the same way:
 *
 *   - A customer wants the refund policy at the moment they are about to pay,
 *     not after going back to the landing page to look for it. Every screen in
 *     the checkout is a screen where "what happens if this comes out wrong?" is
 *     the live question.
 *   - Payment gateways and marketplaces check that these links are reachable
 *     from the pages where money changes hands. A footer only on `/` passes a
 *     glance and fails a real review.
 *
 * The link list is generated from LEGAL_DOCUMENTS, so a policy cannot be added
 * to the route table and forgotten here.
 */
/**
 * Every link in the footer, so the policy row and the contact row cannot drift
 * apart in height or underline.
 *
 * 44px tall on touch (WCAG 2.5.5) and relaxed to a text line on desktop, where
 * the pointer minimum is 24px (2.5.8) and `py-1` clears it. Keeping 44px rows
 * on a mouse-driven screen is what made this footer 200px tall.
 */
const FOOTER_LINK =
  'inline-flex min-h-touch items-center text-ink-muted underline underline-offset-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 lg:min-h-0 lg:py-1';

export function SiteFooter() {
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-line bg-surface">
      {/* Two inline rows rather than two labelled columns. The headings
          ("POLICIES", "CONTACT") were four lines of chrome around seven links
          that already say what they are, and the stacked list made the footer
          taller than some of the pages under it. On a wide screen the two rows
          sit side by side and the whole thing is one line high. */}
      <div className="mx-auto flex w-full max-w-shell flex-col gap-x-6 gap-y-2 px-4 py-4 text-sm lg:flex-row lg:items-center lg:justify-between lg:px-6">
        {/* The visible heading is gone; the label is not. A screen-reader user
            still gets "Policies" as the landmark's name. */}
        <nav aria-label="Policies">
          {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- Safari/VoiceOver drops list semantics when list-style is none; see Stepper. */}
          <ul className="flex list-none flex-wrap items-center gap-x-4" role="list">
            {LEGAL_DOCUMENTS.map((doc) => (
              <li key={doc.slug}>
                <Link to={paths.legal[doc.slug]} className={FOOTER_LINK}>
                  {doc.navLabel}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex flex-wrap items-center gap-x-4">
          {/* Both real links, not plain text: on a phone this is the
              difference between raising a grievance and copying an address out
              by hand, and `tel:` makes the number one tap from any screen. */}
          <address className="flex flex-wrap items-center gap-x-4 not-italic">
            <a href={`mailto:${LEGAL_CONTACT.email}`} className={FOOTER_LINK}>
              {LEGAL_CONTACT.email}
            </a>
            <a href={LEGAL_CONTACT.phoneHref} className={FOOTER_LINK}>
              {LEGAL_CONTACT.phone}
            </a>
          </address>
          <p className="text-xs text-ink-subtle">
            &copy; {year} {LEGAL_CONTACT.entity}
          </p>
        </div>
      </div>
    </footer>
  );
}
